import { AsyncPipe } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import {
  OnyxDropdownComponent,
  OnyxSectionComponent,
  OnyxTabsComponent,
} from '@onyx/angular';
import { forkJoin, map } from 'rxjs';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import { ValidationService } from '../../../../common/services/validation.service';
import { FleetCategory } from '../../../fleet/common/enums/fleet-category';

type DriverSkillsFormGroup = ReturnType<
  (typeof DriverSkillsFormComponent)['buildForm']
>;

@Component({
  selector: 'app-driver-skills-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxTabsComponent,
    AsyncPipe,
  ],
  templateUrl: './driver-skills-form.component.html',
  styleUrl: './driver-skills-form.component.scss',
})
export class DriverSkillsFormComponent {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly validationService = inject(ValidationService);

  public readonly form = input.required<DriverSkillsFormGroup>();

  protected readonly I18N = 'drivers.driverForm.skills';

  protected readonly languages$ = this.dictionariesService.getDictionary(
    DictionaryCode.LANGUAGE,
  );
  protected readonly driverTechnicalSkills$ =
    this.dictionariesService.getDictionary(
      DictionaryCode.DRIVER_TECHNICAL_SKILL,
    );
  protected readonly semiTrailerTypes$ = forkJoin([
    this.dictionariesService.getDictionary(DictionaryCode.VEHICLE_TYPE),
    this.validationService.getFleetValidation(FleetCategory.SEMI_TRAILER),
  ]).pipe(
    map(([vehicleTypes, validation]) => {
      const semiTrailerTypes = Object.keys(
        validation[FleetCategory.SEMI_TRAILER],
      );
      return vehicleTypes.filter(({ value }) =>
        semiTrailerTypes.includes(value),
      );
    }),
  );
  protected readonly driverExperienceLevels$ = this.dictionariesService
    .getDictionary(DictionaryCode.DRIVER_EXPERIENCE_LEVEL)
    .pipe(
      map((options) =>
        options.map((option, index) => ({ ...option, name: `${index + 1}` })),
      ),
    );

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      languages: fb.control<string[] | null>(null),
      technicalSkills: fb.control<string[] | null>(null),
      semiTrailers: fb.control<string[] | null>(null),
      experienceLevel: fb.control<string | null>(null),
    });
  }
}
